import { useEffect, useState, useCallback, useRef } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { BarChart, Loader2, Wand2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSEODashboard() {
  const { authFetch } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);
  const [log, setLog] = useState([]);
  const abortRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/seo-dashboard`);
    if (r.ok) setData(await r.json());
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const runAIFix = async () => {
    const problems = (data?.problems || []);
    if (!problems.length) return toast.info('Nema članaka za popravak');
    setFixing(true);
    abortRef.current = false;
    setLog([`▶ Pokrenut AI fix za ${problems.length} članaka (Gemini 2.5 Flash)…`]);

    let ok = 0, fail = 0;
    for (let i = 0; i < problems.length; i++) {
      if (abortRef.current) { setLog(l => [...l, '⏸ Prekinuto od strane korisnika. Nastavak moguć — stanje je spremljeno.']); break; }
      const a = problems[i];
      setLog(l => [...l, `[${i + 1}/${problems.length}] ${a.title_hr?.slice(0, 60) || a.slug}…`]);
      try {
        const r = await authFetch(`${API}/admin/content-health/quick-fix`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ article_ids: [a.id] }),
        });
        if (r.ok) { ok++; setLog(l => [...l, `  ✓ popravljeno`]); }
        else { fail++; setLog(l => [...l, `  ✗ greška ${r.status}`]); }
      } catch (e) { fail++; setLog(l => [...l, `  ✗ ${e.message}`]); }
    }
    setLog(l => [...l, `── Gotovo. Popravljeno: ${ok}, greške: ${fail}.`]);
    setFixing(false);
    load();
  };

  if (loading || !data) return <div className="p-10"><Loader2 className="animate-spin text-zinc-500" /></div>;

  return (
    <div className="p-4 md:p-6" data-testid="admin-seo-dashboard">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <BarChart className="text-red-500" size={24} />
          <div>
            <h1 className="text-2xl font-bold">SEO nadzorna ploča</h1>
            <p className="text-sm text-zinc-400">Zdravlje meta podataka, slika i naslova — povezano s pravim člancima.</p>
          </div>
        </div>
        <button onClick={runAIFix} disabled={fixing || (data.problems || []).length === 0}
                data-testid="seo-ai-fix-btn"
                className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm font-bold disabled:opacity-40">
          {fixing ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          AI popravi sve ({(data.problems || []).length})
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Stat label="Objavljenih" value={data.total} accent="blue" />
        <Stat label="Bez meta opisa" value={data.missing_meta_description} accent="amber" />
        <Stat label="Bez glavne slike" value={data.missing_featured_image} accent="amber" />
        <Stat label="Prekratkih naslova" value={data.short_titles} />
        <Stat label="Predugačkih naslova" value={data.long_titles} />
        <Stat label="Zdravlje" value={`${data.health_score}%`} accent={data.health_score >= 80 ? 'green' : 'amber'} />
      </div>

      {log.length > 0 && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 mb-6 font-mono text-[11px] max-h-56 overflow-y-auto" data-testid="seo-log">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs uppercase text-zinc-400">AI log</span>
            {fixing && <button onClick={() => { abortRef.current = true; }} className="text-xs text-amber-400 hover:underline">Prekini</button>}
          </div>
          {log.map((l, i) => <div key={i} className="text-zinc-300">{l}</div>)}
        </div>
      )}

      <h2 className="text-sm uppercase font-bold text-zinc-400 mb-2">Problematični članci ({(data.problems || []).length})</h2>
      <div className="rounded-lg border border-zinc-800 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-900 text-xs uppercase text-zinc-400">
            <tr>
              <th className="px-3 py-2 text-left">Naslov</th>
              <th className="px-3 py-2 text-left">Slika</th>
              <th className="px-3 py-2 text-left">Meta / excerpt</th>
              <th className="px-3 py-2">Akc.</th>
            </tr>
          </thead>
          <tbody>
            {(data.problems || []).map(p => (
              <tr key={p.id} className="border-t border-zinc-800" data-testid={`seo-row-${p.id}`}>
                <td className="px-3 py-1.5 max-w-[320px] truncate">{p.title_hr}</td>
                <td className="px-3 py-1.5">{p.cover_image_url ? <span className="text-emerald-400">✓</span> : <span className="text-red-400">✗</span>}</td>
                <td className="px-3 py-1.5">{p.meta_description_hr || p.excerpt_hr ? <span className="text-emerald-400">✓</span> : <span className="text-red-400">✗</span>}</td>
                <td className="px-3 py-1.5 text-right">
                  <Link to={`/admin/articles/${p.id}/edit`} className="text-xs text-blue-400 hover:underline">Uredi</Link>
                </td>
              </tr>
            ))}
            {(data.problems || []).length === 0 && <tr><td colSpan={4} className="text-center py-8 text-emerald-400">Sve čisto!</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = 'blue' }) {
  const col = { blue: 'text-blue-400', amber: 'text-amber-400', green: 'text-emerald-400' };
  return (
    <div className="p-3 rounded-lg bg-zinc-900 border border-zinc-800">
      <p className="text-[10px] uppercase text-zinc-500">{label}</p>
      <p className={`text-2xl font-bold ${col[accent] || 'text-white'}`}>{value}</p>
    </div>
  );
}
